import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Text,
  Heading,
  VStack,
  HStack,
  useColorMode,
  Center,
  Alert,
  AlertIcon,
  Button,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Book } from "../libs/types";
import { getBooksByUserId, deleteBook } from "../libs/qazbilim-backend";
import { useAuthStore } from "../store/authStore";
import { useBookStore } from "../store/bookStore";
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import BookCard from "./BookCard";
import LoadingLogo from "./LoadingLogo";

function BookCardBox({
  book,
  colorMode,
  handleBookClick,
  navigate,
  onDeleteClick,
}: {
  book: Book;
  colorMode: "light" | "dark";
  handleBookClick: (book: Book) => void;
  navigate: (path: string) => void;
  onDeleteClick: (book: Book) => void;
}) {
  const { t } = useTranslation();
  return (
    <Box
      id={`my-books-card-${book.id}`}
      borderRadius="md"
      overflow="hidden"
      bg={
        colorMode === "light"
          ? "rgba(240, 240, 240, 0.6)"
          : "rgba(30, 35, 45, 0.8)"
      }
      boxShadow="sm"
      cursor="pointer"
      transition="transform 0.2s, box-shadow 0.2s, background-color 0.2s"
      _hover={{
        transform: "translateY(-5px)",
        boxShadow: "lg",
        bg:
          colorMode === "light"
            ? "rgba(248, 248, 248, 0.9)"
            : "rgba(38, 46, 63, 0.9)",
      }}
      _active={{
        transform: "translateY(-2px)",
        boxShadow: "md",
      }}
      onClick={() => handleBookClick(book)}
      width={{ base: "80%", md: "auto" }}
      mx={{ base: "auto", md: "0" }}
    >
      <Box
        id={`my-books-card-cover-${book.id}`}
        position="relative"
        width="100%"
        mx="auto"
        height="220px"
        mb={2}
        onClick={() => handleBookClick(book)}
        cursor="pointer"
      >
        <BookCard
          id={`my-books-card-bookcard-${book.id}`}
          title={book.title}
          coverUrl={book.coverUrl}
          width="100%"
          height="220px"
          boxShadow="md"
          borderRadius="md"
        />
      </Box>
      <VStack
        id={`my-books-card-meta-${book.id}`}
        p={3}
        align="start"
        spacing={2}
      >
        <Heading
          id={`my-books-card-title-${book.id}`}
          size="md"
          noOfLines={1}
          w="100%"
        >
          {book.title}
        </Heading>
        <Text
          id={`my-books-card-author-${book.id}`}
          color={colorMode === "light" ? "gray.500" : "gray.400"}
          fontSize="sm"
          w="100%"
        >
          {book.author}
        </Text>
        <HStack spacing={2} w="100%" minW={0}>
          <Button
            id={`my-books-card-update-${book.id}`}
            size="sm"
            variant="outline"
            leftIcon={<EditIcon />}
            flex={1}
            minW={0}
            overflow="hidden"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/update-book/${book.id}`);
            }}
          >
            <Box
              as="span"
              display="block"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {t("myBooks.update")}
            </Box>
          </Button>
          <IconButton
            id={`my-books-card-delete-${book.id}`}
            aria-label={t("myBooks.delete")}
            size="sm"
            variant="outline"
            colorScheme="red"
            flexShrink={0}
            icon={<DeleteIcon />}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick(book);
            }}
          />
        </HStack>
      </VStack>
    </Box>
  );
}

const MyBooks: React.FC = () => {
  const { colorMode } = useColorMode();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { userId, isAuthenticated } = useAuthStore();
  const [books, setBooks] = useState<Book[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookToDelete, setBookToDelete] = useState<Book | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Open newly created book when navigated from CreateBook with state.openBookId
  useEffect(() => {
    const openBookId = (location.state as { openBookId?: string } | null)
      ?.openBookId;
    if (openBookId) {
      navigate(`/read/${openBookId}`, { replace: true });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setError(null);
    setBooks([]);
    setIsLoading(true);
    getBooksByUserId(userId)
      .then((data) => {
        if (!cancelled) {
          setBooks(Array.isArray(data) ? data : []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load books");
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, isAuthenticated]);

  const handleBookClick = (book: Book) => {
    useBookStore.getState().resetState();
    navigate(`/read/${book.id}`);
  };

  const handleDeleteClick = (book: Book) => {
    setBookToDelete(book);
    onOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!bookToDelete) return;
    setIsDeleting(true);
    try {
      await deleteBook(bookToDelete.id);
      setBooks((prev) => prev.filter((b) => b.id !== bookToDelete.id));
      setBookToDelete(null);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("myBooks.deleteError"));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setBookToDelete(null);
    onClose();
  };

  if (!isAuthenticated || !userId) {
    return (
      <Center id="my-books-login-guard" minH="300px">
        <VStack id="my-books-login-guard-stack" spacing={4}>
          <Text id="my-books-login-guard-message">
            {t("myBooks.loginRequired")}
          </Text>
          <Button
            id="my-books-login-button"
            onClick={() => navigate("/login")}
            colorScheme="blue"
          >
            {t("menu.login")}
          </Button>
        </VStack>
      </Center>
    );
  }

  if (isLoading) {
    return (
      <Center id="my-books-loading" minH="400px">
        <VStack id="my-books-loading-stack" spacing={4}>
          <LoadingLogo id="my-books-loading-logo" size="xl" />
          <Text id="my-books-loading-text">{t("myBooks.loading")}</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Box
      id="my-books-page"
      p={{ base: 0, md: 6 }}
      maxW="container.md"
      mx="auto"
    >
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={handleDeleteCancel}
        id="my-books-delete-dialog"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader id="my-books-delete-dialog-header">
              {t("myBooks.deleteConfirmTitle")}
            </AlertDialogHeader>
            <AlertDialogBody id="my-books-delete-dialog-body">
              {t("myBooks.deleteConfirmMessage", {
                title: bookToDelete?.title ?? "",
              })}
            </AlertDialogBody>
            <AlertDialogFooter id="my-books-delete-dialog-footer">
              <Button
                ref={cancelRef}
                onClick={handleDeleteCancel}
                id="my-books-delete-dialog-cancel"
              >
                {t("myBooks.deleteConfirmCancel")}
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConfirm}
                isLoading={isDeleting}
                loadingText={t("myBooks.deleteConfirmDeleting")}
                id="my-books-delete-dialog-confirm"
                ml={3}
              >
                {t("myBooks.deleteConfirmDelete")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Box id="my-books-content" px={{ base: 1, md: 0 }}>
        <Heading
          id="my-books-title"
          size={{ base: "md", md: "lg" }}
          mb={{ base: 4, md: 6 }}
          color={colorMode === "light" ? "gray.800" : "white"}
        >
          {t("myBooks.title")}
        </Heading>

        {error && (
          <Alert
            id="my-books-error-alert"
            status="warning"
            mb={{ base: 3, md: 6 }}
            borderRadius="md"
            bg={colorMode === "light" ? undefined : "rgba(45, 55, 72, 0.8)"}
            color={colorMode === "light" ? undefined : "white"}
          >
            <AlertIcon id="my-books-error-icon" />
            <Text id="my-books-error-text">{t("error.bookLoadFail")}</Text>
          </Alert>
        )}

        {books.length === 0 && !error ? (
          <Center
            id="my-books-empty-state"
            minH={{ base: "150px", md: "200px" }}
            bg={
              colorMode === "light"
                ? "rgba(240, 240, 240, 0.6)"
                : "rgba(30, 35, 45, 0.8)"
            }
            borderRadius="md"
            p={{ base: 3, md: 4 }}
          >
            <VStack id="my-books-empty-stack" spacing={4}>
              <Text id="my-books-empty-text" fontSize="lg">
                {t("myBooks.empty")}
              </Text>
              <Button
                id="my-books-create-button"
                colorScheme="blue"
                onClick={() => navigate("/create")}
                size="md"
              >
                {t("menu.createBook")}
              </Button>
            </VStack>
          </Center>
        ) : (
          <VStack
            id="my-books-sections"
            align="stretch"
            spacing={{ base: 6, md: 8 }}
          >
            {(() => {
              const publishedBooks = books.filter((b) => b.published === true);
              const draftBooks = books.filter((b) => b.published !== true);
              const cardProps = {
                colorMode,
                handleBookClick,
                navigate,
                onDeleteClick: handleDeleteClick,
              };
              return (
                <>
                  {publishedBooks.length > 0 && (
                    <Box id="my-books-section-published">
                      <Heading
                        id="my-books-heading-published"
                        size="sm"
                        mb={3}
                        color={colorMode === "light" ? "gray.700" : "gray.300"}
                      >
                        {t("myBooks.publishedSection", "Published")}
                      </Heading>
                      <Grid
                        id="my-books-grid-published"
                        templateColumns={[
                          "repeat(1, 1fr)",
                          "repeat(2, 1fr)",
                          "repeat(3, 1fr)",
                        ]}
                        gap={{ base: 3, md: 6 }}
                        px={{ base: 1, md: 0 }}
                      >
                        {publishedBooks.map((book) => (
                          <BookCardBox
                            key={book.id}
                            book={book}
                            {...cardProps}
                          />
                        ))}
                      </Grid>
                    </Box>
                  )}
                  {draftBooks.length > 0 && (
                    <Box id="my-books-section-drafts">
                      <Heading
                        id="my-books-heading-drafts"
                        size="sm"
                        mb={3}
                        color={colorMode === "light" ? "gray.700" : "gray.300"}
                      >
                        {t("myBooks.draftsSection", "Drafts")}
                      </Heading>
                      <Grid
                        id="my-books-grid-drafts"
                        templateColumns={[
                          "repeat(1, 1fr)",
                          "repeat(2, 1fr)",
                          "repeat(3, 1fr)",
                        ]}
                        gap={{ base: 3, md: 6 }}
                        px={{ base: 1, md: 0 }}
                      >
                        {draftBooks.map((book) => (
                          <BookCardBox
                            key={book.id}
                            book={book}
                            {...cardProps}
                          />
                        ))}
                      </Grid>
                    </Box>
                  )}
                </>
              );
            })()}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default MyBooks;
